import clsx from "clsx";
import { Fragment } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { Label } from "@fronterahealth/frontera-ui-components";

import { FormInputField } from "@components/AssessmentFormRenderer/components/FormInputField";
import { FormTableSelectCell } from "@components/AssessmentFormRenderer/components/FormTableSelectCell";
import { ScoreTableSection } from "@components/AssessmentFormRenderer/types/layoutTypes";
import { getNestedError } from "@components/AssessmentFormRenderer/utils/getNestedError";
import { HorizontalScrollHint } from "@components/HorizontalScrollHint/HorizontalScrollHint";

interface FormScoreTableProps {
  section: ScoreTableSection;
}

export const FormScoreTable: React.FC<FormScoreTableProps> = ({ section }) => {
  const fieldPath = section.source;
  const { formState, control } = useFormContext();
  const columns = section.columns || [];

  const { fields } = useFieldArray({
    control,
    name: fieldPath,
  });

  return (
    <div className="flex flex-col gap-2">
      <Label displayType="loud">{section.label}</Label>
      {/* A single shared grid (header + every row as direct children) so column
          tracks stay aligned across rows. `max-content` columns size to the
          widest cell in that column -- normally the header label, since
          editable inputs are width:100% and stretch to fill the resolved
          column width rather than contributing to it. A column that
          declares `width` gets that Tailwind width class applied to its
          header AND data cells, so every cell in the track agrees on a
          fixed size for `max-content` to resolve to -- this is what lets a
          long dropdown label (e.g. BHI's "Ast. Today") get real room
          instead of being clamped to its short header text. */}
      <HorizontalScrollHint>
        <div className="grid w-fit gap-y-2" style={{ gridTemplateColumns: `repeat(${columns.length}, max-content)` }}>
          {/* Column separation comes from cell padding (px-2), not a grid
              gap -- a grid gap would leave an uncolored strip between
              columns that breaks up each row's background stripe below. */}
          {columns.map((col) => (
            <div key={col.key} className={clsx("px-2 pb-1", col.width)}>
              <Label displayType="normal">{col.label}</Label>
            </div>
          ))}
          {/* Spans every column track so the header separator reads as one
              continuous line instead of breaking across the column gaps. */}
          <div className="border-b border-gray-200" style={{ gridColumn: "1 / -1" }} />
          {fields.map((field, rowIndex) => (
            <Fragment key={field.id}>
              {columns.map((col) => {
                const fieldFullPath = `${fieldPath}.${rowIndex}.${col.key}`;
                const error = getNestedError(formState.errors, fieldFullPath);
                const inputType = col.validation?.inputType ?? "text";
                const rowKey = col.optionsByRow
                  ? String((field as Record<string, unknown>)[col.optionsByRow.keyColumn])
                  : undefined;
                const rowOptions = (rowKey != null ? col.optionsByRow?.optionsMap[rowKey] : undefined) ?? col.options;

                return (
                  <div
                    key={col.key}
                    className={clsx("flex items-center px-2 py-1", col.width, rowIndex % 2 === 1 && "bg-gray-50")}
                  >
                    {rowOptions ? (
                      <FormTableSelectCell
                        fieldPath={fieldFullPath}
                        options={rowOptions}
                        editable={col.editable}
                        error={error}
                      />
                    ) : (
                      <FormInputField
                        fieldPath={fieldFullPath}
                        inputType={inputType}
                        editable={col.editable}
                        error={error}
                        validation={col.validation}
                      />
                    )}
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </HorizontalScrollHint>
    </div>
  );
};
